import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  Moon, 
  Compass, 
  Heart,
  Video,
  Music,
  Download,
  ExternalLink,
  Church,
  Share2,
  Cross,
  Sparkles,
  ArrowRight,
  User,
  Target
} from 'lucide-react';
import { retreatContent } from '../data/retreatContent';

const ResourcesPage = () => {
  const navigate = useNavigate();
  const shareUrl = typeof window !== 'undefined' ? window.location.origin : 'https://lenten-journey-3.preview.emergentagent.com';

  return (
    <div className="px-4 py-6 max-w-2xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
          <BookOpen className="w-4 h-4" />
          Spiritual Resources
        </div>
        <h1 className="font-heading text-2xl text-[var(--primary)] font-bold">Resources & Guides</h1>
        <p className="text-[var(--text-muted)] text-sm mt-1">Everything you need for your journey</p>
      </div>

      {/* ============================================ */}
      {/* OUR SPIRITUAL FOUNDATION - Main Content */}
      {/* ============================================ */}
      <div className="bg-white rounded-2xl border border-[var(--border)] shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] p-5 text-white">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">
            <Cross className="w-5 h-5" />
            Our Spiritual Foundation
          </h2>
        </div>
        
        <div className="p-6 space-y-6 text-[var(--foreground)] leading-relaxed">
          <p>
            The <strong>Spiritual Exercises of St. Ignatius</strong> serve as the focal point of this retreat due to Divine Mercy University's deep spiritual roots. The university's foundation and guiding light are closely tied to the <strong>Regnum Christi Movement</strong> and the <strong>Legionaries of Christ</strong>, for which Ignatian spirituality is a fundamental pillar.
          </p>
          <p>
            This spiritual tradition enriches the daily prayer lives of the university's founders and many of the faculty, staff, and supporters. Consequently, these Ignatian principles are woven into the very fabric of the university, reflecting directly in the integral formation of its students, staff, and faculty.
          </p>
        </div>
      </div>

      {/* The 30-Day Retreat */}
      <div className="bg-white rounded-2xl border border-[var(--border)] shadow-sm overflow-hidden">
        <div className="bg-[var(--secondary)]/20 p-5 border-b border-[var(--border)]">
          <h3 className="font-heading text-lg font-bold text-[var(--foreground)] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[var(--secondary)]" />
            The 30-Day Retreat
          </h3>
        </div>
        
        <div className="p-6 text-[var(--foreground)] leading-relaxed">
          <p>
            Traditionally, the Spiritual Exercises are experienced as a <strong>silent, 30-day retreat</strong> away from the world. This journey is divided into four "weeks" or phases, guiding the retreatant through:
          </p>
          <ul className="mt-4 space-y-2 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-[var(--secondary)]">•</span>
              A deepening awareness of God's love
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[var(--secondary)]">•</span>
              A confrontation with sin
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[var(--secondary)]">•</span>
              A journey alongside Christ in His life and passion
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[var(--secondary)]">•</span>
              A sharing in His glorious resurrection
            </li>
          </ul>
          <p className="mt-4">
            This app adapts that profound month-long journey into a focused, accessible <strong>Lenten format</strong>.
          </p>
        </div>
      </div>

      {/* The 19th Annotation */}
      <div className="bg-white rounded-2xl border border-[var(--border)] shadow-sm overflow-hidden">
        <div className="bg-[var(--primary)]/10 p-5 border-b border-[var(--border)]">
          <h3 className="font-heading text-lg font-bold text-[var(--foreground)] flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[var(--primary)]" />
            The 19th Annotation (A Retreat in Daily Life)
          </h3>
        </div>
        
        <div className="p-6 text-[var(--foreground)] leading-relaxed">
          <p>
            St. Ignatius recognized that not everyone could leave their daily responsibilities for 30 days of silence. In his <strong>19th Annotation</strong>, he provided a framework for a <em>"Retreat in Daily Life"</em>.
          </p>
          <p className="mt-4">
            This allows busy individuals to experience the full transformative power of the Exercises while remaining fully present to their <strong>families, work, and daily duties</strong> by committing to a daily rhythm of prayer and reflection.
          </p>
        </div>
      </div>

      {/* The RIMP Way */}
      <div className="bg-white rounded-2xl border border-[var(--border)] shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] p-5">
          <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
            <Compass className="w-5 h-5" />
            The RIMP Way
          </h3>
        </div>
        
        <div className="p-6 text-[var(--foreground)] leading-relaxed">
          <p>
            This retreat incorporates the <strong>RIMP framework</strong> to map the four traditional "weeks" of the Spiritual Exercises directly to your personal journey of flourishing. It is designed to facilitate deep mission discernment through four key pillars:
          </p>
          
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-4 bg-[var(--primary)]/5 rounded-xl p-4">
              <div className="w-12 h-12 rounded-full bg-[var(--primary)] flex items-center justify-center flex-shrink-0">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-[var(--primary)]">Relationship</h4>
                <p className="text-sm text-[var(--text-muted)]">Who is God to me? (Days 1-7)</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 bg-[var(--secondary)]/10 rounded-xl p-4">
              <div className="w-12 h-12 rounded-full bg-[var(--secondary)] flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-[var(--foreground)]">Identity</h4>
                <p className="text-sm text-[var(--text-muted)]">Who did God make me to be? (Days 8-18)</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 bg-[var(--primary-dark)]/10 rounded-xl p-4">
              <div className="w-12 h-12 rounded-full bg-[var(--primary-dark)] flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-[var(--primary-dark)]">Mission</h4>
                <p className="text-sm text-[var(--text-muted)]">What is God calling me to do? (Days 19-25)</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 bg-[var(--primary)]/5 rounded-xl p-4">
              <div className="w-12 h-12 rounded-full bg-[var(--primary)] flex items-center justify-center flex-shrink-0">
                <Compass className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-[var(--primary)]">Purpose</h4>
                <p className="text-sm text-[var(--text-muted)]">Why am I here? (Days 26-30)</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* ADDITIONAL RESOURCES */}
      {/* ============================================ */}
      
      <div className="border-t border-[var(--border)] pt-8">
        <h2 className="font-heading text-xl text-[var(--primary)] font-bold mb-6 text-center">Additional Resources</h2>
      </div>

      {/* DMU Link */}
      <a 
        href="https://divinemercy.edu/"
        target="_blank"
        rel="noopener noreferrer"
        className="block bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] rounded-2xl p-5 text-white hover:shadow-lg transition-all"
      >
        <div className="flex items-center gap-4">
          <img 
            src={retreatContent.logo}
            alt="DMU"
            className="w-16 h-16 rounded-full border-2 border-white/30"
          />
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg">Divine Mercy University</h3>
            <p className="text-white/80 text-sm">Psychology • Counseling • Spiritual Direction</p>
            <p className="text-[var(--secondary)] text-xs mt-1">divinemercy.edu</p>
          </div>
          <ExternalLink className="w-5 h-5 text-white/70" />
        </div>
      </a>

      {/* QR Code - Share */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5 border-2 border-amber-300">
        <div className="flex items-center gap-4">
          <img 
            src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(shareUrl)}&bgcolor=FFFFFF&color=5C4A68`}
            alt="QR Code to share"
            className="w-28 h-28 rounded-xl bg-white p-2 shadow-md"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Share2 className="w-5 h-5 text-amber-600" />
              <h3 className="font-heading font-bold text-amber-900">Share This Retreat</h3>
            </div>
            <p className="text-amber-700 text-sm mb-2">
              Scan to share with friends & family
            </p>
            <button
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                alert('Link copied!');
              }}
              className="bg-amber-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-amber-600 transition-colors"
            >
              Copy Link
            </button>
          </div>
        </div>
      </div>

      {/* Prayer Media */}
      <div className="space-y-3">
        <h3 className="font-heading font-semibold text-[var(--primary)] px-1 flex items-center gap-2">
          <Music className="w-4 h-4" />
          Prayer Media
        </h3>
        
        <div className="grid grid-cols-2 gap-3">
          <button 
            onClick={() => navigate('/music')}
            className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl p-4 text-white text-center hover:shadow-md transition-all"
          >
            <Music className="w-8 h-8 mx-auto mb-2" />
            <p className="font-semibold text-sm">Sacred Music</p>
            <p className="text-white/70 text-xs">Worship & Meditation</p>
          </button>
          
          <button 
            onClick={() => navigate('/talks')}
            className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl p-4 text-white text-center hover:shadow-md transition-all"
          >
            <Video className="w-8 h-8 mx-auto mb-2" />
            <p className="font-semibold text-sm">Spiritual Talks</p>
            <p className="text-white/70 text-xs">Guided Reflections</p>
          </button>
        </div>
      </div>

      {/* Regnum Christi Resources */}
      <div className="space-y-3">
        <h3 className="font-heading font-semibold text-[var(--primary)] px-1 flex items-center gap-2">
          <Church className="w-4 h-4" />
          Regnum Christi Resources
        </h3>
        
        <a 
          href="https://www.regnumchristi.com/en/"
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-gradient-to-r from-[#1B93A4] to-[#157a8a] rounded-xl p-5 text-white hover:shadow-lg transition-all"
        >
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Church className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-lg">Regnum Christi Resources</h4>
              <p className="text-white/90 text-sm mt-1 leading-relaxed">
                Including: Online retreat guides, in-person retreats and events near you, and many other spiritual, formative and meditation tools for small group and personal use.
              </p>
            </div>
            <ExternalLink className="w-5 h-5 text-white/70 flex-shrink-0 mt-1" />
          </div>
        </a>
      </div>

      {/* PDF Downloads */}
      <div className="space-y-3">
        <h3 className="font-heading font-semibold text-[var(--primary)] px-1 flex items-center gap-2">
          <Download className="w-4 h-4" />
          Downloads
        </h3>
        
        <a 
          href="https://www.regnumchristi.com/en/retreat-guides/"
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl p-5 text-white hover:shadow-lg transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
              <Download className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-lg">Download PDF Guides</h4>
              <p className="text-white/80 text-sm">Retreat guides for offline use</p>
              <p className="text-white/60 text-xs mt-1">Includes video, audio, & questionnaire</p>
            </div>
            <ExternalLink className="w-5 h-5 text-white/70" />
          </div>
        </a>
      </div>

      {/* Footer */}
      <div className="text-center py-6 border-t border-[var(--border)]">
        <p className="text-[var(--text-muted)] text-sm">
          Made with prayer by Divine Mercy University
        </p>
      </div>
    </div>
  );
};

export default ResourcesPage;
